/**
 * RSD Visual Rack Configurator v2.0
 * MTNtek Adventure LLC
 *
 * Vanilla JS — no framework dependencies.
 * Layered CAD image viewport with validated positioning.
 * Component-level inventory, SKU generation, Horizon cart drawer.
 *
 * IMAGE APPROACH:
 * - All images are 16:9 PNGs with transparent backgrounds
 * - Base layers (hitch, mast+crossbar, decal) are same canvas size with built-in positioning
 * - Cradles are rendered in leftmost position, shifted via CSS transform: translate()
 * - Cradle positions were hand-tuned using adjustment sliders and finalized per model
 */

(function () {
  'use strict';

  // ============================================================
  // CONFIG DATA
  // ============================================================

  const RACK_CONFIG = {
    RSD2: { capacity: 2, maxOtb: 1 },
    RSD4: { capacity: 4, maxOtb: 2 },
    RSD5: { capacity: 5, maxOtb: 2 },
    RSD6: { capacity: 6, maxOtb: 3 },
    RSD8: { capacity: 8, maxOtb: 4 }
  };

  const COLORS = [
    { name: 'Desert Sage', value: 'desert-sage', hex: '#738276', sku: 'SAGE', variant: 'Desert Sage', imgKey: 'decalSage' },
    { name: 'River Bed', value: 'river-bed', hex: '#3C5667', sku: 'RVBD', variant: 'River Bed', imgKey: 'decalRiver' }
  ];

  const HITCHES = [
    { name: 'Short', sku: 'SHO', variant: 'Short', imgKey: 'hitchShort' },
    { name: 'Standard', sku: 'STA', variant: 'Standard', imgKey: 'hitchStandard' },
  ];

  const HANDLES = {
    hitchBase: 'rsd-hitch-base',
    mast: 'rsd-mast',
    decal: 'vertical-rack-decal',
    crossbar: { RSD2: 'rsd-2-crossbar-set', RSD4: 'rsd-4-crossbar-set', RSD5: 'rsd-5-crossbar-set', RSD6: 'rsd-6-crossbar-set', RSD8: 'rsd-8-crossbar-set' },
    cradle: { standard: 'rsd-wheel-cradle-standard', xl: 'rsd-wheel-cradle-xl', otb: 'otb-handlebar-cradle' }
  };

  // Cradle positions: validated by hand on 16:9 contain, then recalculated for 4:3 cover.
  // With object-fit: cover on a 4:3 container, 16:9 images are 1.333x wider than viewport.
  // All tx values multiplied by 4/3 to maintain correct spacing.
  // ty values unchanged (height fills exactly with cover for this aspect ratio pair).
  // Fine-tuning may still be needed — these are calculated starting points.
  const CRADLE_POSITIONS = {
    RSD2: [
      { tx: 5.3, ty: 0.1, z: 15 },
      { tx: 19.3, ty: 0.7, z: 14 }
    ],
    RSD4: [
      { tx: 0, ty: 0, z: 15 },
      { tx: 10, ty: 0.3, z: 14 },
      { tx: 19.3, ty: 0.6, z: 13 },
      { tx: 28.7, ty: 0.9, z: 12 }
    ],
    RSD5: [
      { tx: -5.6, ty: -0.4, z: 15 },
      { tx: 4.3, ty: 0.1, z: 14 },
      { tx: 13.7, ty: 0.6, z: 13 },
      { tx: 24, ty: 1.1, z: 12 },
      { tx: 32.7, ty: 1.5, z: 11 }
    ],
    RSD6: [
      { tx: -10.3, ty: -0.4, z: 15 },
      { tx: 0, ty: 0, z: 14 },
      { tx: 10, ty: 0.4, z: 13 },
      { tx: 18.7, ty: 0.8, z: 12 },
      { tx: 28, ty: 1.2, z: 11 },
      { tx: 36.7, ty: 1.7, z: 10 }
    ],
    RSD8: [
      { tx: -13.3, ty: -0.8, z: 15 },
      { tx: -5.3, ty: -0.4, z: 14 },
      { tx: 3.3, ty: 0, z: 13 },
      { tx: 11.3, ty: 0.4, z: 12 },
      { tx: 18.7, ty: 0.8, z: 11 },
      { tx: 26, ty: 1.2, z: 10 },
      { tx: 33.3, ty: 1.6, z: 9 },
      { tx: 40.7, ty: 2, z: 8 }
    ]
  };

  // ============================================================
  // PRODUCT CACHE
  // ============================================================

  const cache = {};

  async function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];
    try {
      const r = await fetch('/products/' + handle + '.js');
      if (!r.ok) throw new Error(r.status);
      const d = await r.json();
      cache[handle] = d;
      return d;
    } catch (e) {
      console.warn('[RSD] fetch failed:', handle, e.message);
      return null;
    }
  }

  function clearCache() { Object.keys(cache).forEach(k => delete cache[k]); }

  // ============================================================
  // DOM HELPER
  // ============================================================

  function h(tag, attrs, kids) {
    const el = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else if (k === 'html') el.innerHTML = v;
        else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
        else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
        else el.setAttribute(k, v);
      });
    }
    if (kids) {
      (Array.isArray(kids) ? kids : [kids]).forEach(c => {
        if (c == null) return;
        el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return el;
  }

  // ============================================================
  // CONFIGURATOR
  // ============================================================

  class Configurator {
    constructor(root) {
      this.root = root;
      this.variantId = root.dataset.variantId;
      this.model = (root.dataset.rackModel || '').toUpperCase();
      this.price = parseFloat(root.dataset.basePrice) || 0;
      this.rack = RACK_CONFIG[this.model];

      if (!this.rack) { root.innerHTML = '<p>Unknown rack model.</p>'; return; }

      // Parse image URLs from data attributes
      this.images = {};
      Array.from(root.attributes).forEach(attr => {
        if (attr.name.startsWith('data-img-')) {
          const key = attr.name.replace('data-img-', '').replace(/-([a-z])/g, (_, c) => c.toUpperCase());
          this.images[key] = attr.value;
        }
      });

      // State
      this.otb = 0;
      this.xl = 0;
      this.color = 'desert-sage';
      this.hitch = 'Standard';
      this.issues = [];
      this.loading = true;
      this.adding = false;

      this.$ = {};
      this.build();
      this.sync();
      this.checkStock();
    }

    get std() { return this.rack.capacity - this.otb - this.xl; }
    get maxXl() { return this.rack.capacity - this.otb; }

    // OTB fills leftmost, XL fills rightmost, Standard fills middle
    getCradleAssignment() {
      const arr = new Array(this.rack.capacity).fill('standard');
      // OTB from left
      for (let i = 0; i < this.otb; i++) {
        arr[i] = 'otb';
      }
      // XL from right
      for (let i = 0; i < this.xl; i++) {
        arr[this.rack.capacity - 1 - i] = 'xl';
      }
      return arr;
    }

    // ----------------------------------------------------------
    // BUILD
    // ----------------------------------------------------------

    build() {
      this.root.innerHTML = '';
      this.root.appendChild(
        h('div', { class: 'rsd-cfg' }, [
          h('div', { class: 'rsd-cfg__builder' }, [
            h('div', { class: 'rsd-cfg__visual' }, [
              this.buildViewport()
            ]),
            h('div', { class: 'rsd-cfg__options' }, [
              this.buildCradles(),
              this.buildColors(),
              this.buildHitches()
            ])
          ]),
          this.buildSummary()
        ])
      );
    }

    // --- Visual Viewport ---

    buildViewport() {
      const layers = [];

      // Layer 1: Hitch bases (z-index 2) — 3 variants, swap on hitch change
      HITCHES.forEach(ht => {
        const src = this.images[ht.imgKey] || '';
        if (src) {
          layers.push(h('img', {
            class: 'rsd-layer rsd-layer--hitch',
            src: src,
            alt: ht.name + ' hitch',
            'data-hitch': ht.name,
            draggable: 'false'
          }));
        }
      });

      // Layer 2: Mast + Crossbars (z-index 3) — static per model, always visible
      if (this.images.mastCrossbars) {
        layers.push(h('img', {
          class: 'rsd-layer rsd-layer--mast rsd-layer--visible',
          src: this.images.mastCrossbars,
          alt: this.model + ' mast and crossbars',
          draggable: 'false'
        }));
      }

      // Layer 3: Decal color (z-index 4) — 2 variants, swap on color change
      COLORS.forEach(c => {
        const src = this.images[c.imgKey] || '';
        if (src) {
          layers.push(h('img', {
            class: 'rsd-layer rsd-layer--decal',
            src: src,
            alt: c.name + ' decal',
            'data-color': c.value,
            draggable: 'false'
          }));
        }
      });

      // Layer 4: Cradles (z-index 5+) — one per position × type
      // All cradle images are full 16:9 frames, positioned via transform: translate()
      const positions = CRADLE_POSITIONS[this.model] || [];
      this.$.cradleEls = {};

      positions.forEach((pos, i) => {
        this.$.cradleEls[i] = {};
        ['standard', 'xl', 'otb'].forEach(type => {
          const imgKey = 'cradle' + type.charAt(0).toUpperCase() + type.slice(1);
          const src = this.images[imgKey] || '';
          if (src) {
            const img = h('img', {
              class: 'rsd-layer rsd-layer--cradle',
              src: src,
              alt: type + ' cradle position ' + (i + 1),
              'data-cradle-pos': String(i),
              'data-cradle-type': type,
              draggable: 'false',
              style: {
                transform: 'translate(' + pos.tx + '%, ' + pos.ty + '%)',
                zIndex: pos.z
              }
            });
            this.$.cradleEls[i][type] = img;
            layers.push(img);
          }
        });
      });

      const viewport = h('div', { class: 'rsd-viewport' }, layers);
      this.$.viewport = viewport;
      return viewport;
    }

    // --- Cradles ---

    buildCradles() {
      return h('div', { class: 'rsd-section' }, [
        h('h2', { class: 'rsd-section__title', text: '1. Configure Your Cradles' }),
        h('p', { class: 'rsd-section__desc', text: this.rack.capacity + ' positions — choose a cradle type for each' }),
        h('div', { class: 'rsd-cradles' }, [
          this.cradleRow('std', 'Standard Wheel Cradles', 'Auto-filled to remaining capacity', true),
          this.cradleRow('otb', 'OTB Handlebar Cradles', 'Up to ' + this.rack.maxOtb + ' — leftmost positions only', false),
          this.cradleRow('xl', 'XL Wheel Cradles', 'For plus-size and fat tires', false)
        ])
      ]);
    }

    cradleRow(key, label, note, readonly) {
      const valEl = h('span', { class: 'rsd-qty__val', text: '0' });
      this.$[key + 'Val'] = valEl;

      if (readonly) {
        return h('div', { class: 'rsd-cradle-row rsd-cradle-row--ro' }, [
          h('div', { class: 'rsd-cradle-row__info' }, [
            h('div', { class: 'rsd-cradle-row__label', text: label }),
            h('div', { class: 'rsd-cradle-row__note', text: note })
          ]),
          h('div', { class: 'rsd-qty rsd-qty--ro' }, [valEl])
        ]);
      }

      const minus = h('button', { type: 'button', class: 'rsd-qty__btn', text: '−', 'aria-label': 'Decrease ' + label, onclick: () => this.adj(key, -1) });
      const plus = h('button', { type: 'button', class: 'rsd-qty__btn', text: '+', 'aria-label': 'Increase ' + label, onclick: () => this.adj(key, 1) });
      this.$[key + 'Minus'] = minus;
      this.$[key + 'Plus'] = plus;

      return h('div', { class: 'rsd-cradle-row' }, [
        h('div', { class: 'rsd-cradle-row__info' }, [
          h('div', { class: 'rsd-cradle-row__label', text: label }),
          h('div', { class: 'rsd-cradle-row__note', text: note })
        ]),
        h('div', { class: 'rsd-qty' }, [minus, valEl, plus])
      ]);
    }

    adj(key, d) {
      if (key === 'otb') {
        this.otb = Math.max(0, Math.min(this.rack.maxOtb, this.otb + d));
        if (this.otb + this.xl > this.rack.capacity) this.xl = this.rack.capacity - this.otb;
      } else if (key === 'xl') {
        this.xl = Math.max(0, Math.min(this.maxXl, this.xl + d));
      }
      this.sync();
      this.checkStock();
    }

    // --- Colors ---

    buildColors() {
      const btns = COLORS.map(c => {
        return h('button', { type: 'button', class: 'rsd-color-btn', 'data-val': c.value, 'aria-label': c.name, onclick: () => { this.color = c.value; this.sync(); this.checkStock(); } }, [
          h('span', { class: 'rsd-color-swatch', style: { backgroundColor: c.hex } }),
          h('span', { class: 'rsd-color-name', text: c.name })
        ]);
      });
      this.$.colorBtns = btns;
      return h('div', { class: 'rsd-section' }, [
        h('h2', { class: 'rsd-section__title', text: '2. Pick Your Color' }),
        h('p', { class: 'rsd-section__desc', text: 'Mast decal color — included in price' }),
        h('div', { class: 'rsd-color-grid' }, btns)
      ]);
    }

    // --- Hitches ---

    buildHitches() {
      const btns = HITCHES.map(ht => {
        return h('button', { type: 'button', class: 'rsd-hitch-btn', 'data-val': ht.name, 'aria-label': ht.name + ' hitch', onclick: () => { this.hitch = ht.name; this.sync(); this.checkStock(); } }, [
          h('span', { class: 'rsd-hitch-name', text: ht.name })
        ]);
      });
      this.$.hitchBtns = btns;
      return h('div', { class: 'rsd-section' }, [
        h('h2', { class: 'rsd-section__title', text: '3. Select Your Hitch' }),
        h('p', { class: 'rsd-section__desc', text: 'Two lengths — same price, pick what fits your vehicle' }),        h('div', { class: 'rsd-hitch-grid' }, btns)
      ]);
    }

    // --- Summary ---

    buildSummary() {
      this.$.sumLines = h('div', { class: 'rsd-sum__lines' });
      this.$.sumPrice = h('div', { class: 'rsd-sum__price' });
      this.$.sumStock = h('div', { class: 'rsd-sum__stock' });
      this.$.sumSku = h('div', { class: 'rsd-sum__sku' });
      this.$.cartBtn = h('button', { type: 'button', class: 'rsd-cart-btn', text: 'Checking availability…', disabled: true, onclick: () => this.addToCart() });

      return h('div', { class: 'rsd-summary' }, [
        h('h3', { class: 'rsd-summary__heading', text: 'Your Configuration' }),
        this.$.sumLines,
        this.$.sumPrice,
        this.$.sumStock,
        this.$.cartBtn,
        this.$.sumSku
      ]);
    }

    // ----------------------------------------------------------
    // SYNC UI + VISUAL
    // ----------------------------------------------------------

    sync() {
      // Cradle counts
      this.$.stdVal.textContent = this.std;
      this.$.otbVal.textContent = this.otb;
      this.$.xlVal.textContent = this.xl;

      // Button states
      if (this.$.otbMinus) this.$.otbMinus.disabled = this.otb <= 0;
      if (this.$.otbPlus) this.$.otbPlus.disabled = this.otb >= this.rack.maxOtb;
      if (this.$.xlMinus) this.$.xlMinus.disabled = this.xl <= 0;
      if (this.$.xlPlus) this.$.xlPlus.disabled = this.xl >= this.maxXl;

      // Active states
      if (this.$.colorBtns) this.$.colorBtns.forEach(b => b.classList.toggle('rsd-color-btn--on', b.dataset.val === this.color));
      if (this.$.hitchBtns) this.$.hitchBtns.forEach(b => b.classList.toggle('rsd-hitch-btn--on', b.dataset.val === this.hitch));

      this.syncViewport();
      this.syncSummary();
    }

    syncViewport() {
      // Hitch layers — show only active hitch
      this.root.querySelectorAll('.rsd-layer--hitch').forEach(img => {
        img.classList.toggle('rsd-layer--visible', img.dataset.hitch === this.hitch);
      });

      // Decal layers — show only active color
      this.root.querySelectorAll('.rsd-layer--decal').forEach(img => {
        img.classList.toggle('rsd-layer--visible', img.dataset.color === this.color);
      });

      // Cradle layers — show correct type at each position
      const assignment = this.getCradleAssignment();
      const positions = CRADLE_POSITIONS[this.model] || [];

      positions.forEach((_, i) => {
        const posEls = this.$.cradleEls[i];
        if (!posEls) return;
        const assignedType = assignment[i] || 'standard';
        ['standard', 'xl', 'otb'].forEach(type => {
          if (posEls[type]) {
            posEls[type].classList.toggle('rsd-layer--visible', type === assignedType);
          }
        });
      });
    }

    syncSummary() {
      const col = COLORS.find(c => c.value === this.color);
      const sku = this.getSKU();

      const lines = [];
      if (this.std > 0) lines.push(this.std + '× Standard Wheel Cradle');
      if (this.otb > 0) lines.push(this.otb + '× OTB Handlebar Cradle');
      if (this.xl > 0) lines.push(this.xl + '× XL Wheel Cradle');
      lines.push(col.name);
      lines.push(this.hitch + ' Hitch');

      this.$.sumLines.innerHTML = '';
      lines.forEach(l => this.$.sumLines.appendChild(h('div', { class: 'rsd-sum__line', text: l })));

      const fmt = this.price > 0 ? '$' + this.price.toLocaleString('en-US') : '';
      this.$.sumPrice.innerHTML = '';
      this.$.sumPrice.appendChild(h('span', { class: 'rsd-sum__price-label', text: 'Total' }));
      this.$.sumPrice.appendChild(h('span', { class: 'rsd-sum__price-val', text: fmt }));

      this.$.sumStock.innerHTML = '';
      if (this.loading) {
        this.$.sumStock.appendChild(h('div', { class: 'rsd-stock rsd-stock--load', text: 'Checking component availability…' }));
      } else if (this.issues.length > 0) {
        this.issues.forEach(msg => this.$.sumStock.appendChild(h('div', { class: 'rsd-stock rsd-stock--warn', text: msg })));
      }

      const btn = this.$.cartBtn;
      const bad = this.issues.length > 0;
      btn.disabled = this.loading || bad || this.adding;
      btn.classList.toggle('rsd-cart-btn--off', bad && !this.loading);
      btn.textContent = this.loading ? 'Checking availability…' : this.adding ? 'Adding to cart…' : bad ? 'Currently unavailable' : 'Add to Cart';

      this.$.sumSku.textContent = sku;
    }

    // ----------------------------------------------------------
    // SKU & PACKING
    // ----------------------------------------------------------

    getSKU() {
      const ht = HITCHES.find(x => x.name === this.hitch);
      const co = COLORS.find(x => x.value === this.color);
      return this.model + '-' + ht.sku + '-' + co.sku + '-' + this.otb + 'OTB-' + this.xl + 'XL-' + this.std + 'STD';
    }

    getPacking() {
      const col = COLORS.find(c => c.value === this.color);
      const items3 = [];
      if (this.std > 0) items3.push({ handle: HANDLES.cradle.standard, desc: 'Standard Wheel Cradle', qty: this.std });
      if (this.otb > 0) items3.push({ handle: HANDLES.cradle.otb, desc: 'OTB Handlebar Cradle', qty: this.otb });
      if (this.xl > 0) items3.push({ handle: HANDLES.cradle.xl, desc: 'XL Wheel Cradle', qty: this.xl });
      return {
        box1: [
          { handle: HANDLES.hitchBase, variant: this.hitch, desc: this.hitch + ' Hitch Base', qty: 1 },
          { handle: HANDLES.mast, desc: 'Mast', qty: 1 },
          { handle: HANDLES.decal, variant: col.variant, desc: col.name + ' Decal', qty: 1 }
        ],
        box2: [{ handle: HANDLES.crossbar[this.model], desc: this.model + ' Crossbar Set', qty: 1 }],
        box3: items3
      };
    }

    // ----------------------------------------------------------
    // INVENTORY
    // ----------------------------------------------------------

    async checkStock() {
      this.loading = true;
      this.issues = [];
      this.syncSummary();

      clearCache();
      const problems = [];
      const col = COLORS.find(c => c.value === this.color);
      const jobs = [];

      jobs.push(fetchProduct(HANDLES.hitchBase).then(p => {
        if (!p) return;
        const v = p.variants.find(x => x.title === this.hitch);
        if (!v || !v.available) problems.push(this.hitch + ' hitch is currently out of stock');
      }));

      jobs.push(fetchProduct(HANDLES.mast).then(p => {
        if (p && !p.available) problems.push('Mast is currently out of stock');
      }));

      jobs.push(fetchProduct(HANDLES.decal).then(p => {
        if (!p) return;
        const v = p.variants.find(x => x.title === col.variant);
        if (!v || !v.available) problems.push(col.name + ' decal is currently out of stock');
      }));

      jobs.push(fetchProduct(HANDLES.crossbar[this.model]).then(p => {
        if (p && !p.available) problems.push(this.model + ' crossbar set is currently out of stock');
      }));

      if (this.std > 0) {
        jobs.push(fetchProduct(HANDLES.cradle.standard).then(p => {
          if (p && !p.available) problems.push('Standard Wheel Cradles are out of stock');
        }));
      }
      if (this.otb > 0) {
        jobs.push(fetchProduct(HANDLES.cradle.otb).then(p => {
          if (p && !p.available) problems.push('OTB Handlebar Cradles are out of stock');
        }));
      }
      if (this.xl > 0) {
        jobs.push(fetchProduct(HANDLES.cradle.xl).then(p => {
          if (p && !p.available) problems.push('XL Wheel Cradles are out of stock');
        }));
      }

      await Promise.all(jobs);
      this.issues = problems;
      this.loading = false;
      this.syncSummary();
    }

    // ----------------------------------------------------------
    // ADD TO CART
    // ----------------------------------------------------------

    async addToCart() {
      if (this.adding) return;
      this.adding = true;
      this.syncSummary();

      const col = COLORS.find(c => c.value === this.color);
      const sku = this.getSKU();
      const cradleParts = [];
      if (this.std > 0) cradleParts.push(this.std + '× Standard Wheel');
      if (this.otb > 0) cradleParts.push(this.otb + '× OTB Handlebar');
      if (this.xl > 0) cradleParts.push(this.xl + '× XL Wheel');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(this.variantId, 10),
              quantity: 1,
              properties: {
                'Cradles': cradleParts.join(' · '),
                'Color': col.name,
                'Hitch Type': this.hitch,
                '_sku': sku,
                '_packing_list': JSON.stringify(this.getPacking())
              }
            }]
          })
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description || 'Could not add to cart');
        }
        // Update Horizon's cart bubble count
        try {
          const cartData = await (await fetch('/cart.js')).json();
          const cartIcon = document.querySelector('cart-icon');
          console.log('[RSD] cart count:', cartData.item_count, 'cartIcon found:', !!cartIcon);
          if (cartIcon) {
            console.log('[RSD] before set:', cartIcon.currentCartCount);
            cartIcon.currentCartCount = cartData.item_count;
            console.log('[RSD] after set:', cartIcon.currentCartCount);
          }
        } catch (e) { console.error('[RSD] bubble update error:', e); }

        this.toast('Configuration added to cart ✓', 'ok');
      } catch (e) {
        console.error('[RSD] cart error:', e);
        this.toast(e.message || 'Something went wrong', 'err');
      } finally {
        this.adding = false;
        this.syncSummary();
      }
    }

    // ----------------------------------------------------------
    // HORIZON CART DRAWER
    // ----------------------------------------------------------

    async openCart() {
      try {
        // Let the theme handle its own drawer — click the cart icon
        // so Horizon fetches fresh content and opens natively.
        const cartTriggers = [
          'cart-icon',
          'a.header__icon[href="/cart"]',
          '.header__icon--cart',
          'button[aria-controls*="cart"]',
          '[data-cart-toggle]',
          '.header a[href="/cart"]'
        ];

        let triggered = false;
        for (const sel of cartTriggers) {
          const el = document.querySelector(sel);
          if (el) {
            const target = el.closest('a, button') || el;
            target.click();
            triggered = true;
            break;
          }
        }

        if (!triggered) window.location.href = '/cart';
      } catch (_) {
        window.location.href = '/cart';
      }
    }

    // ----------------------------------------------------------
    // TOAST
    // ----------------------------------------------------------

    toast(msg, type) {
      const old = document.querySelector('.rsd-toast');
      if (old) old.remove();
      const t = h('div', { class: 'rsd-toast rsd-toast--' + type, text: msg });
      document.body.appendChild(t);
      requestAnimationFrame(() => t.classList.add('rsd-toast--show'));
      setTimeout(() => { t.classList.remove('rsd-toast--show'); setTimeout(() => t.remove(), 300); }, 3500);
    }
  }

  // ============================================================
  // INIT
  // ============================================================

  function init() {
    const el = document.getElementById('rsd-configurator-root');
    if (el) new Configurator(el);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})();